import React, { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

// Actions
import { closeModal } from '../../state/actions/ui/modal';

// Utils
import { showErrorToast } from '../../utils/toasts';

/**
 * Lo que se pinta cuando el modal ha reventado: nada. Cierra el modal, avisa
 * y deja que el límite vuelva a intentarlo — ya sin modal que pintar.
 */
function ModalCrashed({ resetError }: { resetError: () => void }): null {
  const dispatch = useDispatch();
  const { t } = useTranslation(['toasts']);
  useEffect(() => {
    dispatch(closeModal());
    showErrorToast(t('toasts:error.generic'));
    resetError();
    // Una sola vez por fallo: el límite lo desmonta al reintentar.
  }, []);
  return null;
}

/**
 * Límite de errores PROPIO de los modales (QA 15-oct, T2.10+vacío).
 *
 * Un fallo de render dentro de un modal —la celda de fecha de la tabla de
 * «Transferir» con un recorrido sin tramos— subía hasta el límite de la raíz
 * y dejaba la aplicación en blanco: la pantalla de error de la raíz era la
 * del ROUTER (`useRouteError`), y los modales viven fuera del router. Aquí el
 * fallo se queda en el modal: se cierra, sale el aviso de error y la pantalla
 * de debajo sigue como estaba. Sentry lo recibe igual (es su ErrorBoundary).
 */
export function ModalErrorBoundary({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element {
  return (
    <Sentry.ErrorBoundary
      fallback={({ resetError }) => <ModalCrashed resetError={resetError} />}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}

export default ModalErrorBoundary;
